import React from 'react';
import { Box } from '@mui/material';
import { MonetizationOn } from '@mui/icons-material';
import { Chip } from 'cprime-foundational-components';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper';
import TypographyMarked from 'components/TypographyMarked/TypographyMarked';
import { formatDataValue } from 'utils/formatFns';
import { getChipSx } from '../utils/utils';
import { booleanToYN, generateTestId } from 'utils/general';
import { chipColorMap } from 'utils/colorMappers';

export const mainVehicleDetailsFields = {
  dealerPresold: {
    label: 'Dealer PreSold',
    getValue: (data) => data?.main?.vehicleDetails?.presoldType,
    formatValue: (value) => booleanToYN(value) || 'N/A',
  },
  dealerReservedIndicatorChip: {
    label: 'Reserved',
    getValue: (data) => data?.main?.vehicleDetails?.dealerReservedIndicator,
    formatValue: (value, options = {}) => {
      if (!value) {
        return null;
      }

      return (
        <TooltipWrapper tooltipText="Reserved">
          <Box display="flex" alignItems="center">
            <Chip
              data-testid={generateTestId('VehicleRecord', 'VehicleDetailsHeader', 'reserved-chip')}
              label={
                options.isMobile ? (
                  ''
                ) : (
                  <TypographyMarked
                    variant="label"
                    component="span"
                    value="Reserved"
                    fieldId="vehicle-hero-reserved"
                    searchContextValues={options.searchContextValues}
                    isPrint={options.isPrint}
                  />
                )
              }
              icon={
                options.isMobile ? <MonetizationOn color={chipColorMap.currentSaleText} /> : null
              }
              color={chipColorMap.currentSaleText}
              backgroundColor={chipColorMap.currentSaleBackground}
              sx={getChipSx(options)}
            />
          </Box>
        </TooltipWrapper>
      );
    },
  },
  exterior: {
    label: 'Exterior',
    getValue: (data) => ({
      code: data?.main?.vehicleDetails?.exteriorColorCode,
      description: data?.main?.vehicleDetails?.exteriorColorDescription,
    }),
    formatValue: ({ code, description }) => {
      const formattedCode = formatDataValue({ value: code });
      const formattedDescription = formatDataValue({ value: description });
      return `${formattedCode} | ${formattedDescription}`;
    },
  },
  exteriorColor: {
    label: 'Exterior',
    getValue: (data) => ({
      code: data?.main?.vehicleDetails?.exteriorColorCode,
      description: data?.main?.vehicleDetails?.exteriorColorDescription,
    }),
    formatValue: ({ code, description }) => {
      const formattedCode = formatDataValue({ value: code });
      const formattedDescription = formatDataValue({ value: description });
      return `${formattedCode} | ${formattedDescription}`;
    },
    withCopyIcon: true,
    getCopyValue: (data) => {
      const code = formatDataValue({ value: data?.main?.vehicleDetails?.exteriorColorCode });
      const description = formatDataValue({
        value: data?.main?.vehicleDetails?.exteriorColorDescription,
      });
      return `${code} | ${description}`;
    },
  },
  factory: {
    label: 'Factory',
    getValue: (data) => data?.main?.vehicleDetails?.parentAccessoriesFactoryFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  interior: {
    label: 'Interior',
    getValue: (data) => ({
      code: data?.main?.vehicleDetails?.interiorTrimColorCode,
      description: data?.main?.vehicleDetails?.interiorTrimColorName,
    }),
    formatValue: ({ code, description }) => {
      const formattedCode = formatDataValue({ value: code });
      const formattedDescription = formatDataValue({ value: description });
      return `${formattedCode} | ${formattedDescription}`;
    },
  },
  interiorColor: {
    label: 'Interior',
    getValue: (data) => ({
      code: data?.main?.vehicleDetails?.interiorTrimColorCode,
      description: data?.main?.vehicleDetails?.interiorTrimColorName,
    }),
    formatValue: ({ code, description }) => {
      const formattedCode = formatDataValue({ value: code });
      const formattedDescription = formatDataValue({ value: description });
      return `${formattedCode} | ${formattedDescription}`;
    },
    withCopyIcon: true,
    getCopyValue: (data) => {
      const code = formatDataValue({ value: data?.main?.vehicleDetails?.interiorTrimColorCode });
      const description = formatDataValue({
        value: data?.main?.vehicleDetails?.interiorTrimColorName,
      });
      return `${code} | ${description}`;
    },
  },
  katashiki: {
    label: 'Katashiki',
    getValue: (data) => data?.generalInfo?.vehicleAttrs?.katashikiDescription,
    formatValue: (value) => formatDataValue({ value }),
  },
  ppo: {
    label: 'PPO',
    getValue: (data) => data?.main?.vehicleDetails?.parentAccessoriesPostProductionFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  presoldType: {
    label: 'Pre-Sold',
    getValue: (data) => data?.main?.vehicleDetails?.presoldType,
    formatValue: (value, options = {}) => {
      if (value !== 'Y') {
        return null;
      }

      return (
        <TooltipWrapper tooltipText="Pre-Sold">
          <Box display="flex" alignItems="center">
            <Chip
              data-testid={generateTestId('VehicleRecord', 'VehicleDetailsHeader', 'pre-sold-chip')}
              label={
                options.isMobile ? (
                  ''
                ) : (
                  <TypographyMarked
                    variant="label"
                    component="span"
                    value="Pre-Sold"
                    fieldId="vehicle-hero-pre-sold"
                    searchContextValues={options.searchContextValues}
                    isPrint={options.isPrint}
                  />
                )
              }
              icon={
                options.isMobile ? <MonetizationOn color={chipColorMap.currentSaleText} /> : null
              }
              color={chipColorMap.currentSaleText}
              backgroundColor={chipColorMap.currentSaleBackground}
              sx={getChipSx(options)}
            />
          </Box>
        </TooltipWrapper>
      );
    },
  },
  salesSuffixCode: {
    label: 'Sales Suffix Code',
    getValue: (data) => data?.main?.vehicleDetails?.salesSuffixCode,
    formatValue: (value) => formatDataValue({ value }),
  },
};
